import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOnboarding } from "../contexts/OnboardingContext";
import { LanguageChoice } from "../types/onboarding";

// Import step components
import { Const } from "snudbud-backbone-sdk";
import InterestsStep from "../components/onboarding/InterestsStep";
import KnownLanguagesStep from "../components/onboarding/KnownLanguagesStep";
import LanguagesToLearnStep from "../components/onboarding/LanguagesToLearnStep";
import StudyGoalStep from "../components/onboarding/StudyGoalStep";
import UserProfileStep from "../components/onboarding/UserProfileStep";

type OnboardingStep = "languages-to-learn" | "known-languages" | "study-goal" | "interests" | "profile" | "submitting";

export default function Onboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>("languages-to-learn");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, setData, submitOnboarding } = useOnboarding();

  // Handle progress through the onboarding steps
  const handleLanguagesToLearnContinue = (languages: LanguageChoice[]) => {
    setData((prev) => ({ ...prev, languagesToLearn: languages }));
    setCurrentStep("known-languages");
  };

  const handleKnownLanguagesContinue = (languages: Const.LanguageCode[]) => {
    setData((prev) => ({ ...prev, knownLanguages: languages }));
    setCurrentStep("study-goal");
  };

  const handleStudyGoalContinue = (goal: Const.DailyGoal) => {
    setData((prev) => ({ ...prev, dailyGoal: goal }));
    setCurrentStep("interests");
  };

  const handleInterestsContinue = (interests: string[]) => {
    setData((prev) => ({ ...prev, interests }));
    setCurrentStep("profile");
  };

  const handleProfileContinue = (name: string, profileImage: File | null, description: string) => {
    setData((prev) => ({
      ...prev,
      name,
      profileImage,
      description,
    }));
    setCurrentStep("submitting");

    // Submit onboarding data
    setIsSubmitting(true);
    submitOnboarding()
      .then(() => {
        navigate("/onboarding-complete");
      })
      .catch((error) => {
        console.error("Failed to submit onboarding data:", error);
        setCurrentStep("profile");
        setIsSubmitting(false);
      });
  };

  // Render the current step
  if (isSubmitting || currentStep === "submitting") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Customizing your learning experience...</h2>
          <p className="text-gray-600">Just a moment while we set up your account</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {currentStep === "languages-to-learn" && (
        <LanguagesToLearnStep onContinue={handleLanguagesToLearnContinue} initialValue={data.languagesToLearn} />
      )}

      {currentStep === "known-languages" && (
        <KnownLanguagesStep onContinue={handleKnownLanguagesContinue} initialValue={data.knownLanguages} />
      )}

      {currentStep === "study-goal" && (
        <StudyGoalStep onContinue={handleStudyGoalContinue} initialValue={data.dailyGoal} />
      )}

      {currentStep === "interests" && (
        <InterestsStep onContinue={handleInterestsContinue} initialValue={data.interests} />
      )}

      {currentStep === "profile" && (
        <UserProfileStep
          onContinue={handleProfileContinue}
          initialName={data.name}
          initialDescription={data.description}
        />
      )}
    </>
  );
}
